// src/api/masterdata.js
import { createHttpClient } from "./client";

const masterCache = new Map();
const masterPending = new Map();
const MASTER_REQUEST_DELAY_MS = Number(process.env.REACT_APP_MASTER_REQUEST_DELAY_MS || 100);
const MASTER_MAX_RETRIES = Number(process.env.REACT_APP_MASTER_MAX_RETRIES || 2);

function requestKey(path) {
  return String(path || "");
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function retryDelayMs(err, attempt) {
  const retryAfter = Number(err?.response?.headers?.["retry-after"]);
  if (Number.isFinite(retryAfter) && retryAfter > 0) return retryAfter * 1000;
  return Math.min(1200, 250 * 2 ** attempt);
}

function requestsKey(requests = []) {
  return JSON.stringify(
    (Array.isArray(requests) ? requests : []).map((r) => ({
      key: String(r?.key || ""),
      path: String(r?.path || ""),
      field: String(r?.field || "name"),
      valueField: String(r?.valueField || "id"),
    }))
  );
}

/**
 * ✅ options [{value,label}]
 * - value: id (หรือ documentId ถ้าระบุ)
 * - label: field ที่ใช้แสดง (เช่น name/size)
 */
export function toOptions(list = [], labelField = "name", valueField = "id") {
  return (Array.isArray(list) ? list : [])
    .map((x) => ({
      value: String(x?.[valueField] ?? ""),
      label: String(x?.[labelField] ?? ""),
    }))
    .filter((o) => o.value);
}

function extractArray(resData) {
  if (Array.isArray(resData?.data)) return resData.data;
  if (Array.isArray(resData)) return resData;
  if (resData?.data && typeof resData.data === "object") return [resData.data];
  return [];
}

export async function fetchMaster(path) {
  const k = requestKey(path);
  if (masterCache.has(k)) return masterCache.get(k);
  if (masterPending.has(k)) return masterPending.get(k);

  const http = createHttpClient();
  const job = (async () => {
    let lastErr;
    for (let attempt = 0; attempt <= MASTER_MAX_RETRIES; attempt += 1) {
      try {
        const res = await http.get(path);
        const arr = extractArray(res?.data);
        masterCache.set(k, arr);
        return arr;
      } catch (err) {
        lastErr = err;
        if (err?.response?.status !== 429 || attempt >= MASTER_MAX_RETRIES) break;
        await sleep(retryDelayMs(err, attempt));
      }
    }

    throw lastErr;
  })();

  masterPending.set(k, job);
  try {
    return await job;
  } finally {
    masterPending.delete(k);
  }
}

export async function fetchMasterOptions(request) {
  const arr = await fetchMaster(request?.path);
  const valueField = request?.valueField || "id";
  const labelField = request?.field || "name";
  return toOptions(arr, labelField, valueField);
}

export async function fetchMasters(requests = []) {
  const k = requestsKey(requests);
  if (masterCache.has(k)) return masterCache.get(k);
  if (masterPending.has(k)) return masterPending.get(k);

  const job = (async () => {
    const data = {};
    const list = Array.isArray(requests) ? requests : [];

    for (let i = 0; i < list.length; i += 1) {
      const r = list[i];
      data[r.key] = await fetchMasterOptions(r);
      if (MASTER_REQUEST_DELAY_MS > 0 && i < list.length - 1) {
        await sleep(MASTER_REQUEST_DELAY_MS);
      }
    }

    masterCache.set(k, data);
    return data;
  })();

  masterPending.set(k, job);
  try {
    return await job;
  } finally {
    masterPending.delete(k);
  }
}
